import type { AuditFields, FigureStatus, ISODateString, UUID } from './common';

/**
 * ============================================================================
 * EVENT MODEL
 * ============================================================================
 *
 * This is the source of truth for the entire application (Product +
 * Calculation Specification v1 — Final Draft, §3 / §11 / §15 fixed
 * decision #3). Every screen (Home, Schedule, Analytics, Report) is a
 * DERIVED VIEW computed from this event history — nothing else is
 * authoritative.
 *
 * Shared rules for ALL event types:
 *   - Append-only in principle. "Editing" an event should generally be
 *     modelled as: soft-delete (supersede) the old version, insert a new
 *     one. Phase 1 provides an edit UI that does exactly this under the
 *     hood (see src/lib/db/eventsRepo.ts) rather than mutating rows
 *     in place, so the audit trail survives user corrections too.
 *   - `effectiveDate` is ALWAYS the date the change takes effect for
 *     calculation purposes (e.g. LIC's stated effective date for a rate
 *     change) — this is deliberately distinct from `createdAt` (when the
 *     user happened to type it into the app). The UI must never let
 *     these two be confused.
 *   - Every event links back to the single Loan record via `loanId`.
 *
 * REPAYMENT CONSEQUENCE MODEL (Phase 1 correction):
 * There is deliberately NO standalone "EMI/Tenure Change" event type.
 * EMI and tenure changes are consequences of something else happening —
 * a rate change, a prepayment, or an LIC-side correction — so they are
 * recorded AS PART OF the event that caused them, via the shared
 * `RepaymentConsequence` shape below. This directly encodes the product
 * principle: the user records what they currently believe will happen
 * (status: 'projected') as part of the causing event itself, and later
 * EDITS THAT SAME EVENT to replace it with what LIC actually did (status:
 * 'actual_lic') — there is no separate/parallel record for "actual" vs
 * "projected"; the event IS the source of truth, and its consequence
 * carries a status field indicating which kind of information it holds.
 */

/**
 * The repayment-schedule consequence of a rate change, prepayment, or
 * balance correction: what happens to EMI and/or remaining tenure as a
 * result.
 *
 * PHASE 2: this is no longer a free-text, optional sub-record. Per the
 * approved Phase 2 spec, for the event types where it applies
 * (RateChangeEvent, PrepaymentEvent when a genuine principal reduction,
 * BalanceCorrectionEvent), recording a consequence is MANDATORY — see
 * each event type's own doc comment and src/lib/calc/validation.ts.
 * The engine (src/lib/calc/engine.ts) CALCULATES a suggested value for
 * whichever of newEmi/newRemainingTenureMonths is not `primaryLever`;
 * the user may override either, and `primaryLever` records which one the
 * user most recently edited by hand — this is what prevents an infinite
 * EMI<->tenure recalculation loop (the non-primary value is recalculated
 * from the primary one; editing the non-primary value simply makes IT
 * the new primary, rather than bouncing back and forth).
 *
 * `status` reuses the existing FigureStatus vocabulary ('projected' |
 * 'actual_lic') rather than introducing a parallel enum — 'reconciled'
 * is not meaningful here since a consequence is either what the user
 * currently believes (projected) or what LIC has confirmed (actual_lic).
 */
export interface RepaymentConsequence {
  newEmi?: number;
  newRemainingTenureMonths?: number;
  /**
   * Which of newEmi/newRemainingTenureMonths the user most recently set
   * by hand — the other one is the engine's calculated/suggested value
   * (though the user may also have edited that one previously; only the
   * MOST RECENT edit determines which is "primary" going forward, per
   * the approved no-ping-pong rule).
   */
  primaryLever: 'emi' | 'tenure';
  status: Extract<FigureStatus, 'projected' | 'actual_lic'>;
  /**
   * The date the revised EMI/tenure actually takes effect. Deliberately
   * distinct from the causing event's own `effectiveDate` — e.g. a rate
   * change may be recorded as effective from a certain date, but LIC may
   * not revise the actual EMI/repayment schedule until a later date.
   * Required (see validation.ts) — the UI pre-fills it from the causing
   * event's effectiveDate by default, but it remains independently
   * editable.
   */
  repaymentTermsEffectiveDate: ISODateString;
}

export type LoanEventType =
  | 'disbursement'
  | 'rate_change'
  | 'prepayment'
  | 'balance_correction'
  | 'charge'
  | 'emi_date_change';

/**
 * Base fields shared by every event type. Concrete event interfaces below
 * extend this. `type` is a discriminant field enabling a tagged union
 * (LoanEvent, below) so the UI/engine can switch/narrow on event type
 * safely.
 */
interface BaseLoanEvent extends AuditFields {
  loanId: UUID;
  effectiveDate: ISODateString;
  note?: string;
}

/**
 * A tranche of loan disbursement. Core feature for this under-construction,
 * stage-wise disbursed loan (spec §6/§4 fixed decision #8) — NOT an edge
 * case. Outstanding principal at any point in time is defined as
 * SUM(disbursements up to that date) minus principal reductions; the
 * sanctioned/approved amount is never used directly as outstanding
 * principal.
 */
export interface DisbursementEvent extends BaseLoanEvent {
  type: 'disbursement';
  amount: number;
  trancheLabel?: string; // e.g. "Tranche 3 of 5 — plinth completion"
}

/**
 * An interest rate change, stored with LIC's actual effective date (not
 * the date the user enters it). The calculation engine resolves the
 * applicable rate for any given day by finding the RateChangeEvent with
 * the latest effectiveDate <= that day — never by "switching" a whole
 * EMI cycle at once. See spec §7 / §5.2.
 *
 * PHASE 2: `repaymentConsequence` is now REQUIRED, not optional. A rate
 * change inherently affects repayment terms (spec: "There should be no
 * checkbox asking whether to record EMI/tenure consequences. A rate
 * change inherently affects repayment terms.") — the engine calculates a
 * suggested EMI or tenure automatically; the user picks which lever is
 * primary and may override either value.
 */
export interface RateChangeEvent extends BaseLoanEvent {
  type: 'rate_change';
  annualRatePercent: number;
  repaymentConsequence: RepaymentConsequence;
}

/**
 * A payment the user makes outside the regular EMI flow. Distinguishes a
 * genuine principal prepayment (reduces outstanding principal from its
 * effective date, per spec §8) from an advance EMI payment (pays a future
 * scheduled EMI early, does NOT reduce principal ahead of schedule).
 *
 * PHASE 2: `repaymentConsequence` is REQUIRED when paymentType is
 * 'principal_prepayment' (a genuine principal reduction inherently
 * affects repayment terms, same reasoning as RateChangeEvent), and
 * structurally absent when paymentType is 'advance_emi' (an advance
 * payment does not reduce principal and has no repayment consequence).
 * This is enforced in validation.ts, not encoded as a type-level
 * conditional (TypeScript can't easily express "required only when a
 * sibling field has a specific value" without significant complexity
 * disproportionate to the benefit here).
 *
 * Within the consequence, `intent` captures which lever the user
 * expects/knows was used ('reduce_tenure' keeps EMI the same and
 * shortens tenure; 'reduce_emi' keeps tenure the same and lowers EMI;
 * 'both' means both changed explicitly; 'pending_lic_action' means the
 * user made the prepayment but doesn't yet know what LIC did with it —
 * in that case the consequence still carries the engine's own suggested
 * projection, editable, exactly as the general "calculate first,
 * suggest" principle requires; 'pending_lic_action' only means the
 * user hasn't yet gotten LIC's confirmation, not that no figures exist
 * at all).
 */
export interface PrepaymentEvent extends BaseLoanEvent {
  type: 'prepayment';
  amount: number;
  paymentType: 'principal_prepayment' | 'advance_emi';
  /**
   * Required when paymentType === 'principal_prepayment'. Must be
   * absent/undefined when paymentType === 'advance_emi'.
   */
  repaymentConsequence?: RepaymentConsequence & {
    intent: 'reduce_tenure' | 'reduce_emi' | 'both' | 'pending_lic_action';
  };
}

/**
 * A LIC-confirmed outstanding balance as of a given date, entered from an
 * actual LIC statement. This is a RECONCILIATION event (spec §10): the
 * calculation engine treats it as a hard reset of state at effectiveDate
 * — everything calculated before this date is preserved untouched;
 * everything from this date forward recomputes from this confirmed
 * balance.
 *
 * PHASE 2: `repaymentConsequence` is now REQUIRED, not optional ("If LIC
 * confirms a corrected outstanding balance, repayment consequence is not
 * optional."). The engine calculates a suggested EMI/remaining tenure
 * from the corrected balance and the loan's current repayment state; the
 * user may override either value.
 */
export interface BalanceCorrectionEvent extends BaseLoanEvent {
  type: 'balance_correction';
  licConfirmedBalance: number;
  repaymentConsequence: RepaymentConsequence;
}

/**
 * A charge such as insurance or a processing/admin fee. Kept separate
 * from principal/interest calculations UNLESS `financedIntoPrincipal` is
 * true, in which case it behaves like a mini-disbursement for calculation
 * purposes (spec §5 fixed decision).
 */
export interface ChargeEvent extends BaseLoanEvent {
  type: 'charge';
  amount: number;
  category: 'insurance' | 'processing_fee' | 'other';
  financedIntoPrincipal: boolean;
}

/**
 * A change to which day of the month EMI is due (distinct from an EMI
 * amount change). Normal EMI date is the 7th unless overridden by one of
 * these events (spec §10 fixed decision).
 */
export interface EmiDateChangeEvent extends BaseLoanEvent {
  type: 'emi_date_change';
  newEmiDayOfMonth: number;
}

/**
 * The discriminated union of every event type. The calculation engine
 * (Phase 2) and the Timeline UI (Phase 1) both operate on arrays of this
 * type, narrowing on `.type` as needed.
 */
export type LoanEvent =
  | DisbursementEvent
  | RateChangeEvent
  | PrepaymentEvent
  | BalanceCorrectionEvent
  | ChargeEvent
  | EmiDateChangeEvent;

/**
 * Human-readable labels for each event type, used consistently across the
 * Timeline, forms, and (later) Reports so terminology never drifts
 * between screens.
 */
export const EVENT_TYPE_LABELS: Record<LoanEventType, string> = {
  disbursement: 'Disbursement',
  rate_change: 'Interest Rate Change',
  prepayment: 'Prepayment / Advance Payment',
  balance_correction: 'Balance Correction (LIC Statement)',
  charge: 'Charge (Insurance / Fee)',
  emi_date_change: 'EMI Date Change',
};

export const EVENT_TYPE_DESCRIPTIONS: Record<LoanEventType, string> = {
  disbursement: 'A tranche of the loan amount released by LIC.',
  rate_change: "A change to the loan's interest rate, effective from a specific date.",
  prepayment: 'A principal prepayment or an advance EMI payment.',
  balance_correction: 'An LIC-confirmed outstanding balance used to reconcile the calculated schedule.',
  charge: 'Insurance, processing fee, or other loan-related charge.',
  emi_date_change: 'A change to which day of the month the EMI is due.',
};
